import type { Asset } from '@taki/data-model';
import type { Option } from '@taki/ui';

/**
 * HELPERS METIER PURS de l'app Inventaire.
 *
 * Aucun JSX, aucun acces SDK : ce module ne contient que le vocabulaire du
 * parc (catalogues d'options), la lecture du sac `customFields` et le
 * formatage. Il est importe par l'ecran, par les reglages ET par le jeu de
 * demonstration : un seul endroit ou vivent les mots du metier.
 *
 * MODELE DE DONNEES (contrat commun du pack de demo cinema) : le materiel est
 * un `asset` du socle, specialise par `type: 'materiel'` et par `customFields`.
 * La CATEGORIE (camera, optique, lumiere, son, machinerie) est un ATTRIBUT et
 * non un `type` : les Security Rules rendent `type` immuable apres creation,
 * une categorie mal saisie serait donc incorrigible, et les autres apps du pack
 * devraient connaitre les cinq valeurs pour reconnaitre "notre materiel".
 */

/* ------------------------------------------------------------------ */
/* Le type local pose sur la famille `assets`                          */
/* ------------------------------------------------------------------ */

/** Le `type` LOCAL de l'app (declare dans `taki.app.json`, entities[].types). */
export const MATERIEL_TYPE = 'materiel';

/**
 * Le filtre SYSTEMATIQUE de l'app : le parc cinema coexiste dans l'org avec les
 * assets `cat` des apps de demo existantes. Un chat n'a rien a faire dans un
 * inventaire de loueur, donc toute lecture passe par ici.
 */
export function isMateriel(asset: Asset): boolean {
  return asset.type === MATERIEL_TYPE;
}

/* ------------------------------------------------------------------ */
/* Les catalogues d'options (le vocabulaire du metier)                 */
/* ------------------------------------------------------------------ */

/** Catalogue FIGE des statuts du pack de demo. */
export type MaterielStatus = 'disponible' | 'loue' | 'maintenance' | 'reforme';

/**
 * Les statuts, avec leur TON. Le ton est le seul vocabulaire de couleur partage
 * par les apps : `neutral | accent | success | danger`, jamais une couleur.
 */
export const STATUS_OPTIONS: Option[] = [
  { value: 'disponible', label: 'Disponible', tone: 'success' },
  { value: 'loue', label: 'Loue', tone: 'accent' },
  { value: 'maintenance', label: 'En maintenance', tone: 'danger' },
  { value: 'reforme', label: 'Reforme', tone: 'neutral' },
];

/** Les cinq categories du parc (attribut `customFields.category`). */
export type CategoryId = 'camera' | 'optique' | 'lumiere' | 'son' | 'machinerie';

export const CATEGORY_OPTIONS: Option[] = [
  { value: 'camera', label: 'Camera', tone: 'neutral' },
  { value: 'optique', label: 'Optique', tone: 'neutral' },
  { value: 'lumiere', label: 'Lumiere', tone: 'neutral' },
  { value: 'son', label: 'Son', tone: 'neutral' },
  { value: 'machinerie', label: 'Machinerie', tone: 'neutral' },
];

/** Etat physique de l'exemplaire (attribut `customFields.condition`). */
export type ConditionId = 'neuf' | 'bon' | 'usage' | 'a_reviser';

export const CONDITION_OPTIONS: Option[] = [
  { value: 'neuf', label: 'Neuf', tone: 'success' },
  { value: 'bon', label: 'Bon etat', tone: 'neutral' },
  { value: 'usage', label: 'Usage', tone: 'neutral' },
  { value: 'a_reviser', label: 'A reviser', tone: 'danger' },
];

/*
 * Le menu de la zone 1 ne segmente PLUS le parc par categorie : le gabarit v2
 * en a fait des PORTES (jeux de donnees, declarees dans InventaireScreen.tsx),
 * et les categories sont descendues dans le catalogue de filtres de la porte
 * (settings-defs.ts, derive de CATEGORY_OPTIONS ci-dessus).
 */

/** Le libelle d'une valeur dans un catalogue, ou la valeur brute si inconnue. */
export function labelOf(options: Option[], value: string): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

/* ------------------------------------------------------------------ */
/* Lecture du sac customFields                                         */
/* ------------------------------------------------------------------ */

/**
 * Une valeur texte de `customFields`. Le sac est optionnel et ses valeurs sont
 * des primitives : on rend toujours une chaine, jamais `undefined`.
 */
export function customText(asset: Asset, key: string): string {
  const value = asset.customFields?.[key];
  return typeof value === 'string' ? value : '';
}

/**
 * Une valeur nombre de `customFields`. Rend `null` (jamais `undefined`) quand
 * la valeur manque : c'est ce que `Column.value` attend pour le tri et l'export.
 */
export function customNumber(asset: Asset, key: string): number | null {
  const value = asset.customFields?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/* ------------------------------------------------------------------ */
/* Les champs derives du materiel                                      */
/* ------------------------------------------------------------------ */

/** Le statut courant, chaine libre pilotee par l'app. */
export function statusOf(asset: Asset): string {
  return asset.status ?? '';
}

/** La categorie brute (`camera`, `optique`, ...), chaine vide si absente. */
export function categoryOf(asset: Asset): string {
  return customText(asset, 'category');
}

/** La categorie en clair ("Camera"), pour les colonnes et la fiche. */
export function categoryLabel(asset: Asset): string {
  const value = categoryOf(asset);
  return value === '' ? '' : labelOf(CATEGORY_OPTIONS, value);
}

/**
 * L'avatar de la ligne : les DEUX premieres lettres de la categorie
 * (CA, OP, LU, SO, MA). Un materiel n'a pas de photo dans le parc : la pastille
 * dit d'un coup d'oeil de quelle famille il releve.
 */
export function categoryInitials(asset: Asset): string {
  const label = categoryLabel(asset);
  return label === '' ? '?' : label.slice(0, 2).toUpperCase();
}

/** L'etat physique en clair ("Bon etat"), chaine vide si absent. */
export function conditionLabel(asset: Asset): string {
  const value = customText(asset, 'condition');
  return value === '' ? '' : labelOf(CONDITION_OPTIONS, value);
}

/** "Sony FX9" : la marque et le modele en une chaine. */
export function brandModel(asset: Asset): string {
  return [customText(asset, 'brand'), customText(asset, 'model')].filter((part) => part !== '').join(' ');
}

/**
 * Le sous-titre de la liste : "Marque Modele · ref. XXX". Quand rien n'est
 * renseigne, le texte `empty` du binding prend le relais.
 */
export function identityLine(asset: Asset): string {
  const reference = asset.reference ?? '';
  return [brandModel(asset), reference === '' ? '' : `ref. ${reference}`]
    .filter((part) => part !== '')
    .join(' · ');
}

/* ------------------------------------------------------------------ */
/* Formatage                                                           */
/* ------------------------------------------------------------------ */

/** Montants du parc : des euros entiers, jamais de centimes (materiel pro). */
const EURO_FORMAT = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

/** Un montant en euros, ou une chaine vide quand la valeur manque. */
export function formatEuros(value: number | null): string {
  return value == null ? '' : EURO_FORMAT.format(value);
}

const DATE_FORMAT = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

/**
 * Affiche une DATE PURE (`YYYY-MM-DD`, doctrine des dates du socle) dans la
 * locale francaise. On reconstruit la date en heure LOCALE (jamais `new
 * Date(chaine)`, qui interprete la chaine en UTC et fait glisser le jour).
 */
export function formatPureDate(value: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return '';
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  return DATE_FORMAT.format(new Date(year, month - 1, day));
}
